import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Users, Plus, Pencil, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export const DESIGNATION_LABELS = {
  PMO: "PMO", PM: "Product Manager", DEV: "Developer / FDE",
  SOLUTION_ARCHITECT: "Solution Architect", COUNCIL: "AI Council", ADOPTION: "Adoption / CS",
};

const HUMAN_STAGES = ["lab_incubation", "ai_council_review", "production_pod", "adoption_scale"];

export default function Team() {
  const [team, setTeam] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [owners, setOwners] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingOwners, setSavingOwners] = useState(false);
  const [dialog, setDialog] = useState(null); // {mode, member}

  const load = useCallback(() => {
    Promise.all([
      api.get("/team"),
      api.get("/designations"),
      api.get("/settings/stage-owners"),
    ]).then(([t, d, o]) => { setTeam(t.data); setDesignations(d.data); setOwners(o.data); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/team/${id}`); toast.success("Member removed"); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const saveOwners = async () => {
    setSavingOwners(true);
    try { await api.put("/settings/stage-owners", { owners }); toast.success("Default owners saved"); }
    catch { toast.error("Failed to save"); }
    finally { setSavingOwners(false); }
  };

  const memberById = (id) => team.find((m) => m.id === id);

  return (
    <div className="p-6 max-w-5xl">
      <header className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" /> Team & Responsibilities
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Add team members, then set who owns each phase by default. Overridable per project.</p>
        </div>
        <Button data-testid="add-member-btn" onClick={() => setDialog({ mode: "create", member: { name: "", designation: designations[0] || "PM", email: "", avatar_url: "" } })} className="rounded-sm font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> Add Member
        </Button>
      </header>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <section>
            <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Team Members ({team.length})</h3>
            <div className="space-y-2">
              {team.map((m) => (
                <div key={m.id} data-testid={`member-${m.id}`} className="flex items-center gap-3 border border-border bg-card rounded-sm p-3">
                  <Avatar className="w-10 h-10 rounded-sm">
                    <AvatarImage src={m.avatar_url} className="object-cover" />
                    <AvatarFallback className="rounded-sm bg-primary text-black text-xs font-bold">
                      {m.name.split(" ").map((n) => n[0]).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{DESIGNATION_LABELS[m.designation] || m.designation} · {m.email}</p>
                  </div>
                  <button data-testid={`edit-${m.id}`} onClick={() => setDialog({ mode: "edit", member: m })} className="text-muted-foreground hover:text-white transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`delete-${m.id}`} onClick={() => del(m.id)} className="text-muted-foreground hover:text-danger transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {team.length === 0 && <p className="text-sm text-muted-foreground">No team members yet.</p>}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Default Phase Owners</h3>
              <Button data-testid="save-owners-btn" size="sm" onClick={saveOwners} disabled={savingOwners} className="rounded-sm h-7">
                {savingOwners ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}<span className="ml-1.5">Save</span>
              </Button>
            </div>
            <div className="space-y-2">
              {STAGES.filter((s) => HUMAN_STAGES.includes(s.key)).map((s) => (
                <div key={s.key} className="border border-border bg-card rounded-sm p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    <span className="text-sm font-semibold">{s.name}</span>
                  </div>
                  <Select value={owners[s.key] || "none"} onValueChange={(v) => setOwners({ ...owners, [s.key]: v === "none" ? undefined : v })}>
                    <SelectTrigger data-testid={`owner-select-${s.key}`} className="rounded-sm bg-background h-9">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {DESIGNATION_LABELS[m.designation]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <p className="text-xs text-muted-foreground">Idea Initiation & Vector Validation are AI-owned auto-gates — no human owner required.</p>
            </div>
          </section>
        </div>
      )}

      {dialog && <MemberDialog dialog={dialog} designations={designations} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function MemberDialog({ dialog, designations, onClose, onSaved }) {
  const [form, setForm] = useState(dialog.member);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email required"); return; }
    setSaving(true);
    try {
      if (dialog.mode === "create") await api.post("/team", form);
      else await api.put(`/team/${form.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">{dialog.mode === "create" ? "Add team member" : "Edit member"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input data-testid="member-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm bg-background" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Designation</Label>
            <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
              <SelectTrigger data-testid="member-designation-select" className="mt-1.5 rounded-sm bg-background"><SelectValue /></SelectTrigger>
              <SelectContent>
                {designations.map((d) => <SelectItem key={d} value={d}>{DESIGNATION_LABELS[d] || d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input data-testid="member-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm bg-background" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Avatar URL (optional)</Label>
            <Input data-testid="member-avatar-input" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} placeholder="https://…" className="mt-1.5 rounded-sm bg-background" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="member-save-btn" onClick={save} disabled={saving} className="rounded-sm font-semibold">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
